import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./PartnerTable.css";
import { CheckIcon, CircleCheckIcon, CloseIcon, EyeIcon, LinkIcon, SettingsIcon, UnlinkIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { ColDef, GridOptions, LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Permission, PurchaseRuleLink, XolaPartner, XolaPartnerConnectionType, XolaPartnerStatus } from "types";
import { config } from "../../../config";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { NetworkPartnerStatusBadge } from "../PartnerDistributionProfile";
import { DisconnectPartnerModal } from "./DisconnectPartnerModal";
import { api } from "@/api/api";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";

interface PartnerTableProps {
    partners: XolaPartner[];
    onSelectPartners: (selectedPartners: string[]) => void;
    onPartnerClick: (partner: XolaPartner) => void;
}

const useUpdatePartnerStatus = () => {
    return useMutation(async ({ partnerId, status }: { partnerId: ID; status: XolaPartnerStatus }) => {
        return api.patch(`/partners/${partnerId}`, { status });
    });
};

export const PartnerTable = ({ partners, onSelectPartners, onPartnerClick }: PartnerTableProps) => {
    const { isGranted } = useAuthorization();

    const gridApi = useRef<GridApi | null>(null);
    const defaultColDef: ColDef = {
        resizable: true,
        sortable: false,
        filter: false,
        menuTabs: [],
        suppressMovable: true,
        suppressAutoSize: true,
        flex: 1,
    };

    const onGridReady = (params: GridReadyEvent) => {
        // Save the API and column API for later use
        gridApi.current = params.api;
    };

    // Mutation and invalidation
    const { mutateAsync: updatePartnerStatus } = useUpdatePartnerStatus();
    const invalidate = useInvalidate();

    const handleOnUpdatePartnerStatus = async (partnerId: ID | undefined, status: XolaPartnerStatus) => {
        if (!partnerId) return;
        await updatePartnerStatus({ partnerId, status });
        invalidate("partners");
    };

    const handleOnDisconnectPartner = async (partner: XolaPartner) => {
        handleOnUpdatePartnerStatus(partner?.id, XolaPartnerStatus.DISCONNECTED);
    };

    const columnDefs = [
        {
            ...defaultColDef,
            headerName: "",
            checkboxSelection: isGranted(Permission.OPERATORS_ACTIONS)
                ? (params: any) =>
                      params.data.status === XolaPartnerStatus.ACCEPTED ||
                      params.data.status === XolaPartnerStatus.PENDING
                : false,
            maxWidth: 32,
            cellClass: "partner-table-checkbox-cell",
        },
        {
            ...defaultColDef,
            headerName: "Operator Name",
            field: "name",
            minWidth: 250,
            maxWidth: 350,
            cellRenderer: "nameCellRenderer",
            cellClass: "partner-table-name-cell no-translate",
        },
        {
            ...defaultColDef,
            headerName: "You Resell",
            field: "sellerResells",
            minWidth: 100,
            maxWidth: 150,
            cellRenderer: "sellerResellsRenderer",
            cellClass: "seller-resell-cell mx-2",
        },
        {
            ...defaultColDef,
            headerName: "Operator Resells",
            field: "partnerResells",
            maxWidth: 150,
            minWidth: 100,
            cellRenderer: "partnerResellsRenderer",
            cellClass: "operator-resell-cell mx-2",
        },
        {
            ...defaultColDef,
            headerName: "My Terms Template",
            field: "purchaseRuleLink",
            cellRenderer: "termsTemplateRenderer",
            minWidth: 300,
            cellClass: "my-term-template-cell",
        },
        {
            ...defaultColDef,
            headerName: "Status",
            field: "status",
            maxWidth: 150,
            minWidth: 100,
            cellRenderer: "statusRenderer",
            cellRendererParams: (params: { data: XolaPartner }) => ({
                data: params.data,
            }),
            cellClass: "status-cell",
        },
        {
            ...defaultColDef,
            headerName: "Action",
            field: "actions",
            maxWidth: 150,
            minWidth: 100,
            cellRenderer: "actionsCellRenderer",
            hide: !isGranted(Permission.OPERATORS_ACTIONS),
            cellRendererParams: {
                onDisconnectPartner: handleOnDisconnectPartner,
                onPartnerClick: onPartnerClick,
            },
        },
    ];

    const onSelectionChanged = () => {
        const selectedNodes = gridApi.current?.getSelectedNodes();

        const selectedData = selectedNodes?.map((node) => node.data);
        if (selectedData) {
            onSelectPartners(selectedData.map((data) => data.id));
        }
    };

    const components = {
        nameCellRenderer: NameCellRenderer,
        sellerResellsRenderer: SellerResellsRenderer,
        partnerResellsRenderer: PartnerResellsRenderer,
        statusRenderer: StatusRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        termsTemplateRenderer: TermsTemplateRenderer,
    };

    const gridOptions: GridOptions = {
        onSelectionChanged: onSelectionChanged,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
    };
    const formattedPartnerData = partners.map((partner) => formatPartnerData(partner));

    return (
        <ErrorBoundary>
            <div className="ag-theme-alpine ui-partner-table" style={{ height: "100vh" }}>
                <AgGridReact
                    suppressContextMenu
                    suppressCellFocus
                    theme="legacy"
                    rowData={formattedPartnerData}
                    columnDefs={columnDefs}
                    components={components}
                    headerHeight={32}
                    gridOptions={gridOptions}
                    onGridReady={onGridReady}
                />
            </div>
        </ErrorBoundary>
    );
};

const formatPartnerData = (partner: XolaPartner) => {
    const { connectionType, partnerSeller } = partner;
    const sellerResells =
        connectionType === XolaPartnerConnectionType.SUPPLIER ||
        connectionType === XolaPartnerConnectionType.SUPPLIER_RESELLER;
    const partnerResells =
        connectionType === XolaPartnerConnectionType.RESELLER ||
        connectionType === XolaPartnerConnectionType.SUPPLIER_RESELLER;

    return {
        ...partner,
        name: partnerSeller?.name ?? "N/A",
        termTemplate: partner.purchaseRuleLink?.name ?? "N/A",
        sellerResells,
        partnerResells,
    };
};

const NameCellRenderer = ({ value }: { value: string }) => {
    return (
        <div data-testid="name-cell" className="partner-table-name-cell">
            {value}
        </div>
    );
};

const SellerResellsRenderer = ({ value }: { value: boolean }) => {
    return (
        <span className="seller-resell-cell" data-testid="seller-resells">
            {value ? <CircleCheckIcon className="text-green" /> : <CloseIcon className="text-red" />}
        </span>
    );
};

const TermsTemplateRenderer = ({ value }: { value: PurchaseRuleLink }) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();

    const isTermsTemplateAvailable = isGranted(Permission.OPERATORS_ACTIONS);

    const handleClick = () => {
        if (isTermsTemplateAvailable) {
            const termsUrl = `/distribution/termsTemplates/${value.id}`;
            history.push(termsUrl);
        }
    };

    return (
        <NoTranslate className="terms-template-cell text-gray-darker " data-testid="terms-template">
            {value ? (
                <span className={clsx(isTermsTemplateAvailable && "cursor-pointer underline")} onClick={handleClick}>
                    {value.name}
                </span>
            ) : (
                <span>-</span>
            )}
        </NoTranslate>
    );
};

const PartnerResellsRenderer = ({ value }: { value: boolean }) => {
    return (
        <span className="operator-resell-cell" data-testid="operator-resells">
            {value ? <CircleCheckIcon className="text-green" /> : <CloseIcon className="text-red" />}
        </span>
    );
};

const StatusRenderer = ({ value, data }: { value: string; data: XolaPartner }) => {
    const isInitiator = data.seller?.id === data.initiator?.id;
    return <NetworkPartnerStatusBadge status={value} isInitiator={isInitiator} />;
};

const ActionsCellRenderer = ({
    data,
    onPartnerClick,
    onDisconnectPartner,
}: {
    data: XolaPartner;
    onPartnerClick: (partner: XolaPartner) => void;
    onDisconnectPartner: (partner: XolaPartner) => void;
}) => {
    const history = useHistory();
    const disconnectPartnerModal = useModal();
    const handleOnViewPartner = (partner: XolaPartner) =>
        history.push(`/distribution/operators/${partner.id}/partnerProfile`);
    const handleOnPreviewPartnerConnection = (partner: XolaPartner) => onPartnerClick(partner);
    const handleOnDisconnectPartnerConfirm = () => {
        onDisconnectPartner(data);
        disconnectPartnerModal.hide();
    };

    const renderActions = () => {
        switch (data.status) {
            case XolaPartnerStatus.ACCEPTED:
                return (
                    <span className="space-x-1" data-testid="accepted-partner-actions">
                        <SecondaryButton title="Settings" size="tiny" onClick={() => handleOnViewPartner(data)}>
                            <SettingsIcon />
                        </SecondaryButton>
                        <SecondaryButton
                            title="Disconnect"
                            size="tiny"
                            id={`${data.id}-disconnect-action-button`}
                            onClick={disconnectPartnerModal.show}
                        >
                            <UnlinkIcon />
                        </SecondaryButton>
                        <DisconnectPartnerModal
                            isVisible={disconnectPartnerModal.isVisible}
                            data={data}
                            onConfirm={handleOnDisconnectPartnerConfirm}
                            onClose={disconnectPartnerModal.hide}
                        />
                    </span>
                );
            case XolaPartnerStatus.PENDING:
                // If the seller is the initiator, display a "View" button instead of options to accept or decline terms.
                if (data.initiator.id === data.seller.id) {
                    return (
                        <span className="space-x-1" data-testid="view-button-actions">
                            <SecondaryButton
                                title="View"
                                size="tiny"
                                onClick={() => handleOnPreviewPartnerConnection(data)}
                            >
                                <EyeIcon />
                            </SecondaryButton>
                        </span>
                    );
                }

                return (
                    <span className="space-x-1" data-testid="pending-partner-actions">
                        <Button
                            title="Accept"
                            size="tiny"
                            color="success"
                            onClick={() => handleOnPreviewPartnerConnection(data)}
                        >
                            <CheckIcon className="text-white" />
                        </Button>
                        <Button
                            title="Decline"
                            size="tiny"
                            color="danger"
                            onClick={() => handleOnPreviewPartnerConnection(data)}
                        >
                            <CloseIcon className="text-white" />
                        </Button>
                    </span>
                );
            case XolaPartnerStatus.REQUESTED:
            case XolaPartnerStatus.DECLINED:
            case XolaPartnerStatus.ARCHIVED:
                return (
                    <span className="space-x-1" data-testid="default-partner-actions">
                        <SecondaryButton
                            title="View"
                            size="tiny"
                            onClick={() => handleOnPreviewPartnerConnection(data)}
                        >
                            <EyeIcon />
                        </SecondaryButton>
                    </span>
                );
            case XolaPartnerStatus.DISCONNECTED:
                return (
                    <span className="space-x-1" data-testid="disconnected-partner-actions">
                        <SecondaryButton title="Connect" size="tiny" onClick={() => onPartnerClick(data)}>
                            <LinkIcon />
                        </SecondaryButton>
                    </span>
                );
            default:
                return null;
        }
    };

    return <div className="actions-cell pl-1">{renderActions()}</div>;
};

LicenseManager.setLicenseKey(config.agGrid.key);
